import React, { useRef, useCallback, useEffect } from "react";
import { GoogleMap, useJsApiLoader } from "@react-google-maps/api";
import { Box, CircularProgress, Typography } from "@mui/material";
import { googleMapsApiKey } from "config/envConfig";

const libraries = ["marker"];

const GenericMapWithMarkers = ({
  markers = [],
  center,
  zoom = 14,
  onMapLoad,
}) => {
  const mapRef = useRef(null);
  const markersRef = useRef([]);

  const { isLoaded, loadError } = useJsApiLoader({
    googleMapsApiKey,
    libraries,
    language: "en",
  });

  const clearMarkers = useCallback(() => {
    markersRef.current.forEach((m) => {
      m.setMap(null);
    });
    markersRef.current = [];
  }, []);

  const createMarkerHTML = useCallback((iconUrl) => {
    const markerDiv = document.createElement("div");
    markerDiv.style.width = "30px";
    markerDiv.style.height = "30px";
    markerDiv.style.backgroundImage = `url(${iconUrl})`;
    markerDiv.style.backgroundSize = "cover";
    return markerDiv;
  }, []);

  const updateMarkers = useCallback(() => {
    if (!mapRef.current || !window.google?.maps?.marker?.AdvancedMarkerElement)
      return;

    clearMarkers(); // clear prev

    markers.forEach(({ position, icon }) => {
      const marker = new window.google.maps.marker.AdvancedMarkerElement({
        position,
        map: mapRef.current, // put the newst on the map
        content: icon ? createMarkerHTML(icon) : null,
      });
      markersRef.current.push(marker);
    });
  }, [markers, clearMarkers, createMarkerHTML]);

  useEffect(() => {
    updateMarkers();
  }, [markers, updateMarkers]);

  const handleMapLoad = useCallback(
    (map) => {
      mapRef.current = map;
      updateMarkers();
      if (onMapLoad) onMapLoad(map);
    },
    [updateMarkers, onMapLoad]
  );

  if (loadError) {
    return <div>Error loading map: {loadError.message}</div>;
  }

  if (!isLoaded || !center) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          flexDirection: "column",
          width: "100%",
          height: "100%",
        }}
      >
        <CircularProgress color="inherit" />
        <Typography variant="h6" sx={{ mt: 2 }}>
          Loading map and data...
        </Typography>
      </Box>
    );
  }

  return (
    <GoogleMap
      mapContainerStyle={{ width: "100%", height: "100%" }}
      center={center}
      zoom={zoom}
      options={{
        mapId: "mapId",
        disableDefaultUI: true,
        clickableIcons: false,
        gestureHandling: "auto",
        zoomControl: true,
      }}
      onLoad={handleMapLoad}
    />
  );
};

export default GenericMapWithMarkers;
